import * as querystring from 'querystring';

import * as HTTPUtils from '../../util/http_utils';
import * as config from '../../config';
import * as Logger from '../../util/logger_utils';
import * as Browser from '../browser/browser';
import * as SP_CONSTANTS from '../../starzplayer_constants';

export const SPLUNK_LEVELS = {
    error: 'error',
    warning: 'warning',
    info: 'info',
    verbose: 'verbose',
    debug: 'debug'
};

/**
 * Please refer to this document in case you want to make changes to the reportURI: https://starzplay.atlassian.net/wiki/display/DOT/Error+logging+and+monitoring+to+splunk
 */
export default {

    /**
     * Sends an error to splunk.
     * @param {Object} error
     * @param {string} error.message - The message of the error message shown to the user
     * @param {string} error.title - The title of the error message shown to the user
     * @param {string} error.code - The associated error code
     * @param {string} [error.dev] - More information about the error. Not shown to user, but sent to backend. Can be object or string
     * @param {string} [error.level="error"] - Splunk level, SPLUNK_LEVELS contains the possible options.
     */
    send(error) {
        if (!this.sessionToken) {
            console.warn('Error is not send to Splunk because the sessiontoken is not present.');
            return;
        }
        error.level = this._getSplunkLevel(error.level);

        this.addOptions(error);
        const body = JSON.parse(JSON.stringify(this._getSplunkBody()).replace('[]', 'false'));

        HTTPUtils.post({
            uri: `${config.esbUrl}reports/portal?${querystring.stringify({
                env: this._getSplunkEnvironment(),
                event: 'Player',
                level: error.level
            })}`,
            body,
            headers: {
                'Authorization': this.sessionToken
            }
        }, function(success, data) {
            !success && console.error('Could not report to splunk: ', data);
        });
    },

    /**
     * Populates the Splunk object. If the option does not exist, it will be ignored.
     * Adds one or more values by Key value, and merges them with the current instance.
     * @param {Object} options
     * @param {string} [options.userId] the global user ID of the user
     * @param {Object} [options.adapter] the adapter, e.g. Bitmovin Adapter
     * @param {string} [options.feed] the URI to the feed
     * @param {string} [options.videoType] the videoType, e.g. series
     * @param {string} [options.sessionToken] the session token for sending requests to the ESB
     * @param {string} [options.mpxToken] the media/the platform/mpx token
     * @param {Object} [options.clientConfiguration] the initialization configuration
     * @param {Object} [options.starzplayerConfiguration] the starzplayer Configuration
     * @example
     */
    addOptions(options) {
        Object.assign(this, options);
    },

    /**
     * Gets and validates the error levels. Returns 'error' if not set.
     * @param level
     * @private
     */
    _getSplunkLevel(level = 'error') {
        if (level && level in SPLUNK_LEVELS) {
            return level;
        } else if (level) {
            console.warn(`Error level '${level}' not found in allowed splunk levels: ${Object.keys(SPLUNK_LEVELS)}. Using default: 'error'`);
        }
        return 'error';
    },

    /**
     * Converts the environment to Splunk environment
     * @example _getSplunkEnvironment("production") === "prd"
     * @return {string} splunkEnvironment
     * @throws Exception if the environment is invalid
     * @private
     */
    _getSplunkEnvironment() {
        switch (config.environment) {
            case 'prd':
            case 'production':
                return 'prd';
            case 'tst':
            case 'test':
                return 'tst';
            default:
                Logger.warn('Environment ' + config.environment + 'Does not exist. Using: development');
            case 'dev':
            case 'development':
                return 'dev';
        }
    },

    /**
     * Populates and returns the splunk body, ready to sent to the report endpoint
     * Side note: As the file is minified, it is useless to add the stacktrace to the file.
     * @return {{eventId: string, report, parameter: {client: {browser: {string}, version: {string}, userAgent: (WorkerNavigator|Navigator|string), link: (Location|String|string), timestamp: number}, player: {adapter: string, adapterVersion: *, starzPlayerVersion: string, duration: *, videoType, feed}, user: {error: {message, title, code}, userId, sessionToken, mpxToken: null}, developer: {environment: string, message: (*|string), clientConfiguration: (*|{videoType?, mpxToken, feed, sessionToken, fullScreen, adapter, season?, episode?, latest?, startAt?, css?, eventHandlers?, language?, forceFlash?: Boolean}|null), starzPlayerConfiguration: null}}, status: string, level: string}}
     * @private
     */
    _getSplunkBody() {
        const browser = Browser.currentBrowser();
        const starzplayConfigurationClone = Object.assign({}, this.starzplayerConfiguration);

        return {
            'eventId': 'PlayerError',
            'report': this.code.toString(),
            'parameter': {
                client: {
                    browser: browser && browser.name,
                    version: browser && browser.version,
                    userAgent: navigator && navigator.userAgent,
                    link: location && location.href,
                    timestamp: Date && new Date().getTime()
                },
                player: {
                    adapter: this.adapter && this.adapter.getName(),
                    adapterVersion: this.adapter && this.adapter.getVersion(),
                    starzPlayerVersion: `StarzPlayer-v${SP_CONSTANTS.VERSION}`,
                    duration: this.adapter && this.adapter.getCurrentTime(),
                    videoType: this.videoType,
                    feed: this.feed
                },
                user: {
                    error: {
                        message: this.message,
                        title: this.title,
                        code: this.code
                    },
                    userId: this.userId,
                    sessionToken: this.sessionToken,
                    mpxToken: null

                },
                developer: {
                    environment: config.environment,
                    message: this.dev,
                    clientConfiguration: this.clientConfiguration,
                    starzPlayerConfiguration: starzplayConfigurationClone
                }
            },
            'status': this.level,
            'level': this.level
        };
    }
};



// WEBPACK FOOTER //
// ./src/modules/report/splunk.js